import React, { type ReactNode } from 'react';
import { useIntl } from 'react-intl-next';

import { Text } from '@atlaskit/primitives/compiled';
import {
	PopoverContent,
	PopoverProvider,
	PopoverTarget,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightMedia,
	SpotlightPrimaryAction,
	SpotlightStepCount,
} from '@atlaskit/spotlight';
import Image from '@atlaskit/image';

import { useTour } from '../../hooks/useNthUserOrientationStore';
import { spotlight, editorSpaceSpotlight } from '../../messages';
import spacesLight from '../../assets/images/Spaces.gif';
import spacesDark from '../../assets/images/Spaces-dark.png';

interface EditorSpaceSpotlightProps {
	children: ReactNode;
}
const STEP_ID = 'nth-user-orientation-editor-space-spotlight';
/**
 * EditorSpaceSpotlight component that displays a spotlight tutorial for the editor space.
 * It wraps the target element and shows a spotlight popover when the current tour step matches.
 */
export const EditorSpaceSpotlight: React.FC<EditorSpaceSpotlightProps> = ({ children }) => {
	const { reset, next, currentStep, progress } = useTour(STEP_ID);
	const { formatMessage } = useIntl();
	const isVisible = currentStep === STEP_ID;

	return (
		<PopoverProvider>
			<PopoverTarget>{children}</PopoverTarget>
			<PopoverContent
				shouldDismissOnClickOutside={false}
				dismiss={reset}
				next={next}
				placement="right-end"
				offset={[0, 24]}
				isVisible={isVisible}
			>
				<SpotlightCard testId={STEP_ID}>
					<SpotlightHeader>
						<SpotlightHeadline>{formatMessage(editorSpaceSpotlight.headline)}</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightMedia>
						<Image src={spacesLight} srcDark={spacesDark} alt="" />
					</SpotlightMedia>
					<SpotlightBody>
						<Text>{formatMessage(editorSpaceSpotlight.body)}</Text>
					</SpotlightBody>
					<SpotlightFooter>
						<SpotlightStepCount>{formatMessage(spotlight.progress, progress)}</SpotlightStepCount>
						<SpotlightActions>
							<SpotlightPrimaryAction>{formatMessage(spotlight.next)}</SpotlightPrimaryAction>
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>
		</PopoverProvider>
	);
};
